<script setup>
import { ref, computed } from 'vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { useMapGetter } from 'dashboard/composables/store';

const emit = defineEmits(['submit', 'delete']);

const teams = useMapGetter('teams/getTeams');

const dialogRef = ref(null);
const stage = ref(null);
const form = ref({});
const moveToStageId = ref(null);
const siblingStages = ref([]);

const isEdit = computed(() => Boolean(stage.value?.id));

const BEHAVIORS = ['none', 'won', 'lost'];
const STATUSES = ['open', 'pending', 'snoozed', 'resolved'];

const open = (existingStage = null, siblings = []) => {
  stage.value = existingStage;
  siblingStages.value = siblings.filter(s => s.id !== existingStage?.id);
  moveToStageId.value = siblingStages.value[0]?.id || null;
  form.value = {
    name: existingStage?.name || '',
    color: existingStage?.color || '#8B8B8B',
    behavior: existingStage?.behavior || 'none',
    wip_limit: existingStage?.wip_limit || null,
    on_enter: {
      conversation_status: existingStage?.on_enter?.conversation_status || '',
      assign_team_id: existingStage?.on_enter?.assign_team_id || '',
      add_labels: (existingStage?.on_enter?.add_labels || []).join(', '),
    },
  };
  dialogRef.value?.open();
};

const close = () => dialogRef.value?.close();

const onSubmit = () => {
  const payload = {
    name: form.value.name,
    color: form.value.color,
    behavior: form.value.behavior,
    wip_limit: form.value.wip_limit || null,
    on_enter: {
      conversation_status: form.value.on_enter.conversation_status || null,
      assign_team_id: form.value.on_enter.assign_team_id || null,
      add_labels: form.value.on_enter.add_labels
        .split(',')
        .map(label => label.trim())
        .filter(Boolean),
    },
  };
  emit('submit', { id: stage.value?.id, payload });
  close();
};

const onDelete = () => {
  if (!moveToStageId.value) return;
  emit('delete', { id: stage.value.id, moveToStageId: moveToStageId.value });
  close();
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="
      isEdit
        ? $t('KANBAN.STAGE_DIALOG.EDIT_TITLE')
        : $t('KANBAN.STAGE_DIALOG.NEW_TITLE')
    "
    :confirm-button-label="$t('KANBAN.STAGE_DIALOG.SAVE')"
    @confirm="onSubmit"
  >
    <div class="flex flex-col gap-4">
      <label class="flex flex-col gap-1 text-sm text-n-slate-12">
        {{ $t('KANBAN.STAGE_DIALOG.NAME') }}
        <input
          v-model="form.name"
          type="text"
          class="w-full h-9 px-2.5 text-sm rounded-lg reset-base bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
        />
      </label>

      <div class="flex gap-4">
        <label class="flex flex-col gap-1 text-sm text-n-slate-12">
          {{ $t('KANBAN.STAGE_DIALOG.COLOR') }}
          <input
            v-model="form.color"
            type="color"
            class="w-16 h-9 rounded reset-base"
          />
        </label>

        <label class="flex flex-col flex-1 gap-1 text-sm text-n-slate-12">
          {{ $t('KANBAN.STAGE_DIALOG.BEHAVIOR') }}
          <select
            v-model="form.behavior"
            class="w-full h-9 px-2 text-sm rounded-lg reset-base bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak"
          >
            <option
              v-for="behavior in BEHAVIORS"
              :key="behavior"
              :value="behavior"
            >
              {{ $t(`KANBAN.STAGE_DIALOG.BEHAVIORS.${behavior}`) }}
            </option>
          </select>
        </label>

        <label class="flex flex-col gap-1 text-sm text-n-slate-12">
          {{ $t('KANBAN.STAGE_DIALOG.WIP_LIMIT') }}
          <input
            v-model.number="form.wip_limit"
            type="number"
            min="1"
            class="w-24 h-9 px-2.5 text-sm rounded-lg reset-base bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak"
          />
        </label>
      </div>

      <p class="-mt-2 text-xs text-n-slate-10">
        {{ $t('KANBAN.STAGE_DIALOG.WIP_LIMIT_HINT') }}
      </p>

      <fieldset class="flex flex-col gap-3 p-3 border rounded-lg border-n-weak">
        <legend class="px-1 text-xs font-medium text-n-slate-11">
          {{ $t('KANBAN.STAGE_DIALOG.ON_ENTER') }}
        </legend>

        <label class="flex flex-col gap-1 text-sm text-n-slate-12">
          {{ $t('KANBAN.STAGE_DIALOG.SET_STATUS') }}
          <select
            v-model="form.on_enter.conversation_status"
            class="w-full h-9 px-2 text-sm rounded-lg reset-base bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak"
          >
            <option value="">{{ $t('KANBAN.STAGE_DIALOG.NO_CHANGE') }}</option>
            <option v-for="status in STATUSES" :key="status" :value="status">
              {{ $t(`CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.${status}.TEXT`) }}
            </option>
          </select>
        </label>

        <label class="flex flex-col gap-1 text-sm text-n-slate-12">
          {{ $t('KANBAN.STAGE_DIALOG.ASSIGN_TEAM') }}
          <select
            v-model="form.on_enter.assign_team_id"
            class="w-full h-9 px-2 text-sm rounded-lg reset-base bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak"
          >
            <option value="">{{ $t('KANBAN.STAGE_DIALOG.NO_CHANGE') }}</option>
            <option v-for="team in teams" :key="team.id" :value="team.id">
              {{ team.name }}
            </option>
          </select>
        </label>

        <label class="flex flex-col gap-1 text-sm text-n-slate-12">
          {{ $t('KANBAN.STAGE_DIALOG.ADD_LABELS') }}
          <input
            v-model="form.on_enter.add_labels"
            type="text"
            :placeholder="$t('KANBAN.STAGE_DIALOG.LABELS_PLACEHOLDER')"
            class="w-full h-9 px-2.5 text-sm rounded-lg reset-base bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak"
          />
        </label>
      </fieldset>

      <div
        v-if="isEdit && siblingStages.length"
        class="flex items-end gap-2 pt-2 border-t border-n-weak"
      >
        <label class="flex flex-col flex-1 gap-1 text-sm text-n-slate-12">
          {{ $t('KANBAN.STAGE_DIALOG.MOVE_CARDS_TO') }}
          <select
            v-model="moveToStageId"
            class="w-full h-9 px-2 text-sm rounded-lg reset-base bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak"
          >
            <option
              v-for="sibling in siblingStages"
              :key="sibling.id"
              :value="sibling.id"
            >
              {{ sibling.name }}
            </option>
          </select>
        </label>
        <button
          class="h-9 px-3 text-sm rounded-lg text-n-ruby-11 bg-n-ruby-3 hover:bg-n-ruby-4"
          @click="onDelete"
        >
          {{ $t('KANBAN.STAGE_DIALOG.DELETE') }}
        </button>
      </div>
    </div>
  </Dialog>
</template>
